import { useRef } from 'react';
import { generateIdempotencyKey } from '../idempotency';

const STORAGE_KEY = 'dill.idempotencyKeys.v1';

/**
 * How long a key stays reusable.
 *
 * Long enough to cover the reason it is persisted at all — a payment whose
 * response was lost, where the user reopens the modal or reloads before trying
 * again. Short enough that a *deliberate* second payment of the same amount on
 * the same invoice, which looks identical, is not silently collapsed into the
 * first one. Retries happen in seconds; paying the same amount again on purpose
 * essentially never happens inside half an hour.
 */
export const IDEMPOTENCY_KEY_TTL_MS = 30 * 60 * 1000;

interface StoredKey {
  key: string;
  /** Kept in full so a hash collision is detected rather than acted on. */
  signature: string;
  expiresAt: number;
}

type KeyStore = Record<string, StoredKey>;

/** JSON.stringify with object keys sorted, so equal payloads compare equally. */
const stableStringify = (value: any): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value ?? null) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

/** FNV-1a. Only used to keep storage keys short; correctness never rests on it. */
const hashSignature = (signature: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < signature.length; i += 1) {
    hash ^= signature.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

/**
 * sessionStorage is unavailable in some privacy modes and throws when full, so
 * every access degrades to "no stored key" rather than breaking a payment.
 */
const readStore = (): KeyStore => {
  try {
    if (typeof window === 'undefined') {
      return {};
    }
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeStore = (store: KeyStore): void => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    // Nothing to do — the in-memory ref still covers the current mount.
  }
};

const withoutExpired = (store: KeyStore, now: number): KeyStore =>
  Object.entries(store).reduce<KeyStore>((kept, [id, entry]) => {
    if (entry?.expiresAt > now) {
      kept[id] = entry;
    }
    return kept;
  }, {});

/**
 * Holds the Idempotency-Key for a mutating request across retries.
 *
 * The key is bound to the payment it was minted for rather than to the
 * component being mounted. Closing a modal or reloading the page is the most
 * natural way for someone to retry after a spinner dies, and every one of those
 * unmounts this hook — handing the retry a fresh key is exactly how a buyer gets
 * charged twice. So the key is persisted, in sessionStorage, keyed by what the
 * payment *is*.
 *
 * It is dropped on success, and expires after {@link IDEMPOTENCY_KEY_TTL_MS} so
 * that a genuine second payment of the same amount is never mistaken for a
 * retry of the first.
 *
 * Pass the fields that define *which* payment this is. Leave out anything the
 * client regenerates per attempt — card tokens, Plaid tokens, freshly uploaded
 * attachment URLs — or every honest retry will look like a new payment.
 */
export const useIdempotencyKey = () => {
  const heldKey = useRef<{ id: string; key: string; signature: string } | null>(null);

  /** Reuses the key while the payment is the same one, mints a new one when not. */
  const resolveKey = (signatureInput: unknown): string => {
    const signature = stableStringify(signatureInput);
    const id = hashSignature(signature);

    if (heldKey.current?.signature === signature) {
      return heldKey.current.key;
    }

    const now = Date.now();
    const store = withoutExpired(readStore(), now);
    const stored = store[id];
    // The signature is re-checked because `id` is only a hash; on the rare
    // collision this falls through and mints a fresh key.
    if (stored && stored.signature === signature) {
      heldKey.current = { id, key: stored.key, signature };
      writeStore(store);
      return stored.key;
    }

    const key = generateIdempotencyKey();
    heldKey.current = { id, key, signature };
    writeStore({ ...store, [id]: { key, signature, expiresAt: now + IDEMPOTENCY_KEY_TTL_MS } });
    return key;
  };

  /** Call once the payment has landed, so the next one starts fresh. */
  const clearKey = () => {
    const held = heldKey.current;
    heldKey.current = null;
    if (!held) {
      return;
    }
    const store = withoutExpired(readStore(), Date.now());
    delete store[held.id];
    writeStore(store);
  };

  return { resolveKey, clearKey };
};
